import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import {
  FALLBACK_LOCALE,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  matchSupportedLocale,
  type SupportedLocaleCode,
} from "@/i18n/locales";

/**
 * i18next bootstrap.
 *
 * The supported-locale list and the resource bundles both flow from a single
 * source of truth: `apps/web/src/i18n/locales.ts` (the `LOCALE_DEFINITIONS`
 * array) and the JSON files under `src/locales/<code>/translation.json`.
 *
 * Adding a new language X:
 *   1. Drop `src/locales/X/translation.json` mirroring `en`.
 *   2. Add a `LOCALE_DEFINITIONS` entry in `src/i18n/locales.ts`.
 *
 * That's it — `import.meta.glob` (eager) below picks up the new JSON at build
 * time, and the language switcher reads the locale list from `locales.ts`.
 *
 * The parity test (`apps/web/src/locales/locale-parity.test.ts`) blocks
 * merge if the new translation file is missing keys vs. EN OR drops an
 * interpolation token (`{{var}}`).
 *
 * Keys are purpose-keyed per `.claude/skills/frontend-design/SKILL.md` — never
 * content-keyed. That means `home.status` (purpose: the status box) not
 * `home.backend_is_live` (content).
 *
 * Language tags follow BCP 47. Mandarin is registered under its full subtag
 * `zh-CN` (which i18next preserves as the canonical key); a browser sending
 * the bare primary subtag `zh` resolves to `zh-CN` via `matchSupportedLocale`
 * (primary-of-supported fallback). This avoids a 404 at module init time
 * without aliasing the resource map (which complicates per-locale chunk
 * splitting later).
 */

const STORAGE_KEY = "roadbridge.lang";

/**
 * Eagerly load every translation bundle so we can ship one JS chunk in dev
 * and let Vite split per-locale chunks in prod. Each entry is keyed by file
 * path; we map back to a locale code by stripping the prefix and suffix.
 *
 * The `as` cast is the standard Vite pattern (the glob is typed as
 * `Record<string, unknown>` until Vite generates the asset map at build).
 */
const translationModules = import.meta.glob<{ default: Record<string, unknown> }>(
  "./locales/*/translation.json",
  { eager: true },
);

function buildResources(): Record<string, { translation: Record<string, unknown> }> {
  const resources: Record<string, { translation: Record<string, unknown> }> = {};
  for (const [path, mod] of Object.entries(translationModules)) {
    // path looks like: ./locales/en/translation.json
    const match = path.match(/\.\/locales\/([^/]+)\/translation\.json$/);
    if (!match) continue;
    const code = match[1];
    if (!isSupportedLocale(code)) {
      // Translation file exists on disk but is NOT in the supported-locale
      // list. Skip silently — the file is either work-in-progress (Slot A/B/C
      // landing in parallel) or stale. The locale-parity test catches the
      // reverse drift (declared but missing).
      continue;
    }
    resources[code] = { translation: mod.default };
  }
  return resources;
}

/**
 * Detection priority:
 *   1. `?lang=` query param — cross-site handoff from .org; always wins so
 *      clicking any .org CTA opens the app in .org's language.
 *   2. Persisted user choice (`localStorage["roadbridge.lang"]`) — applies
 *      when navigating within the app (no ?lang= present).
 *   3. `rb_lang` cookie — written by .org LanguageSwitcher with consent.
 *   4. `navigator.languages` (or `navigator.language`) walked in order, taking
 *      the first one that maps to a supported locale via `matchSupportedLocale`
 *      (handles `zh-CN` exact, `es-MX` → `es` primary, `zh` → `zh-CN`
 *      primary-of-supported fallback).
 *   5. `FALLBACK_LOCALE` (today: `en`).
 *
 * SSR-safe: returns `FALLBACK_LOCALE` when `navigator` / `localStorage` are
 * absent. The web SPA is client-rendered today so this is mostly defensive.
 */
function detectInitialLocale(): SupportedLocaleCode {
  // (1) ?lang= query param — cross-site handoff from the .org marketing site.
  // Always wins so clicking any .org CTA opens the app in .org's language.
  if (typeof window !== "undefined") {
    try {
      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (urlLang) {
        const matched = matchSupportedLocale(urlLang);
        if (matched) return matched;
      }
    } catch {
      // URL parsing failures — fall through.
    }
  }
  // (2) Persisted choice — applies when navigating within the app (no ?lang=).
  if (typeof localStorage !== "undefined") {
    try {
      const persisted = localStorage.getItem(STORAGE_KEY);
      if (persisted && isSupportedLocale(persisted)) {
        return persisted;
      }
    } catch {
      // localStorage can throw in privacy modes — ignore and fall through.
    }
  }
  // (3) rb_lang cookie — written by the marketing site when the user
  // picks a language there (requires rb_consent=1 cookie acceptance).
  if (typeof document !== "undefined") {
    try {
      const rbLang = document.cookie
        .split(";")
        .find((c) => c.trim().startsWith("rb_lang="))
        ?.split("=")[1];
      if (rbLang) {
        const matched = matchSupportedLocale(rbLang);
        if (matched) return matched;
      }
    } catch {
      // Cookie access blocked (e.g. privacy iframe) — ignore.
    }
  }
  // (4) Browser language preference.
  if (typeof navigator !== "undefined") {
    const candidates =
      navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const tag of candidates) {
      if (!tag) continue;
      const matched = matchSupportedLocale(tag);
      if (matched) return matched;
    }
  }
  return FALLBACK_LOCALE;
}

/**
 * Persist the user's language choice on every i18next `languageChanged`
 * event. Wired in `main.tsx` via the same `i18n.on(...)` pattern that drives
 * font + direction side effects.
 */
export function persistLanguageChoice(lng: string): void {
  if (typeof localStorage === "undefined") return;
  if (!isSupportedLocale(lng)) return;
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    // Same privacy-mode caveat as above.
  }
}

void i18n.use(initReactI18next).init({
  resources: buildResources(),
  lng: detectInitialLocale(),
  fallbackLng: FALLBACK_LOCALE,
  supportedLngs: SUPPORTED_LOCALES as readonly string[] as string[],
  // nonExplicitSupportedLngs is intentionally NOT set here.
  //
  // Setting it to `true` causes i18next to filter a locale by its PRIMARY
  // subtag against supportedLngs instead of the full code. For `zh-CN` the
  // primary is `zh`, which is not in SUPPORTED_LOCALES — so i18next drops
  // `zh-CN` from the resolution chain entirely and falls back to `en`,
  // making all Chinese translations silently invisible.
  //
  // The bare-subtag case (`zh` → `zh-CN`) is handled upstream:
  //   - detectInitialLocale() runs matchSupportedLocale() over navigator.languages
  //   - LanguageSwitcher only offers GA locale codes (never bare `zh`)
  //   - rb_lang cookie is validated with matchSupportedLocale() before use
  interpolation: { escapeValue: false },
  returnNull: false,
});

// Persist the user's choice across reloads. The font + direction side effects
// stay in `main.tsx` so initial bootstrap order is unchanged.
i18n.on("languageChanged", persistLanguageChoice);

export default i18n;
